import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import createClientRequest from '@/proxy';
import { getUserCountryStatus } from '@/api/requestPath';
import { useSubscription } from '@/context';
import { ROUTE_PATHS } from '@/constants/routePaths';
import Client from '@/utils/ClientOld';

// iOS 的 checkApi 内部写死 5s 超时，老 App 不会回调；套 1.5s 抢跑，超时按“不支持”放行
const BRIDGE_PROBE_TIMEOUT = 1500;

const probeCountrySelectBridge = () =>
  Promise.race([
    Client.checkApi('jumpToCountrySelect'),
    new Promise((resolve) => setTimeout(() => resolve(false), BRIDGE_PROBE_TIMEOUT)),
  ]);

/**
 * 进套餐页前的地区判定（套餐页 = 订阅中心）：
 * 已确认国家 → 写国家码 + 渠道判定 → 去套餐页；
 * 未确认但 App 支持地区选择桥 → 去选择国家页；未确认且是老 App → 按非俄进套餐页。
 */
export default function useCountryGate() {
  const { t, i18n } = useTranslation();
  const clientRequest = useMemo(() => createClientRequest(t, i18n), [t, i18n]);
  const { setCountryCode, setV5paySub } = useSubscription();

  // 进行中订阅持 v5pay 渠道 ⇒ 俄区界面；RU + 非 v5pay 进行中订阅 ⇒ 非俄界面
  // sub 由调用方传入接口最新值，不读 context——守卫里 setDetails 尚未提交
  const applyChannel = useCallback(
    (sub, regionCode) => {
      if (sub && sub.status === 4) {
        if (sub.owningPlatform === 'v5pay') setV5paySub(true);
        else if (regionCode === 'RU') setCountryCode('');
      }
    },
    [setCountryCode, setV5paySub],
  );

  // 拉国家状态并写进 context，返回是否已确认（从 App 地区选择页返回后重查也用它）
  const applyConfirmedCountry = useCallback(
    async (sub) => {
      const res = await clientRequest({
        requestPath: getUserCountryStatus,
        requestData: { applicationCode: '70mai' },
      });
      const obj = res?.resultBodyObject;
      if (obj?.countryConfirmed !== true) return false;
      const regionCode = obj.countryCode || '';
      setCountryCode(regionCode);
      applyChannel(sub, regionCode);
      return true;
    },
    [clientRequest, applyChannel, setCountryCode],
  );

  const resolvePlansEntry = useCallback(
    async (sub) => {
      if (await applyConfirmedCountry(sub)) return ROUTE_PATHS.PLAN;
      const hasBridge = await probeCountrySelectBridge();
      return hasBridge ? ROUTE_PATHS.REGION : ROUTE_PATHS.PLAN;
    },
    [applyConfirmedCountry],
  );

  return { applyChannel, applyConfirmedCountry, resolvePlansEntry };
}
